import dayjs from 'dayjs'
import { ID } from '../graphql/IQueryData'
import {
  IAvailabilityBuilding,
  IAvailabilityDay,
} from '../graphql/parkingPool/statParkingPoolAvailability'

export interface ITotals {
  capacity: number
  reserved: number
  used: number
  free: number
}

export const EMPTY_TOTALS: ITotals = {
  capacity: 0,
  reserved: 0,
  used: 0,
  free: 0,
}

const addRow = (totals: ITotals, row: IAvailabilityDay): ITotals => ({
  capacity: totals.capacity + row.capacity,
  reserved: totals.reserved + row.reserved,
  used: totals.used + row.used,
  free: totals.free + row.free,
})

const addTotals = (a: ITotals, b: ITotals): ITotals => ({
  capacity: a.capacity + b.capacity,
  reserved: a.reserved + b.reserved,
  used: a.used + b.used,
  free: a.free + b.free,
})

export const utilizationPct = ({ used, capacity }: ITotals): number | null =>
  capacity > 0 ? Math.round((used / capacity) * 100) : null

const dayKey = (day: string) => dayjs(day).format('YYYY-MM-DD')

export const periodTotals = (stats: IAvailabilityDay[]): ITotals =>
  stats.reduce(addRow, EMPTY_TOTALS)

export interface IDayTotals extends ITotals {
  day: string
}

export const totalsByDay = (stats: IAvailabilityDay[]): IDayTotals[] => {
  const byDay = new Map<string, IDayTotals>()

  stats.forEach(row => {
    const key = dayKey(row.day)
    const current = byDay.get(key) ?? { day: key, ...EMPTY_TOTALS }
    byDay.set(key, { day: key, ...addRow(current, row) })
  })

  return Array.from(byDay.values()).sort((a, b) => a.day.localeCompare(b.day))
}

export interface IPoolParkingTotals extends ITotals {
  poolParkingId: ID
  parkingName: string
  buildings: IAvailabilityBuilding[]
  overCapacity: boolean
}

export const totalsByPoolParking = (
  stats: IAvailabilityDay[],
): IPoolParkingTotals[] => {
  const byParking = new Map<string, IPoolParkingTotals>()

  stats.forEach(row => {
    const key = String(row.poolParkingId)
    const current = byParking.get(key)
    byParking.set(key, {
      poolParkingId: row.poolParkingId,
      parkingName: row.parkingName,
      buildings: current?.buildings.length
        ? current.buildings
        : (row.buildings ?? []),
      overCapacity: (current?.overCapacity ?? false) || row.overCapacity,
      ...addRow(current ?? EMPTY_TOTALS, row),
    })
  })

  return Array.from(byParking.values()).sort((a, b) => b.capacity - a.capacity)
}

export interface IBuildingTotals extends ITotals {
  id: ID | null
  name: string | null
  sort: number
  parkings: IPoolParkingTotals[]
  sharedWith: string[]
  overCapacity: boolean
}

const NO_BUILDING = '__no_building__'

export const totalsByBuilding = (
  stats: IAvailabilityDay[],
): IBuildingTotals[] => {
  const parkings = totalsByPoolParking(stats)
  const byBuilding = new Map<string, IBuildingTotals>()

  parkings.forEach(parking => {
    const buildings: (IAvailabilityBuilding | null)[] = parking.buildings.length
      ? parking.buildings
      : [null]

    buildings.forEach(building => {
      const key = building ? String(building.id) : NO_BUILDING
      const current = byBuilding.get(key) ?? {
        id: building?.id ?? null,
        name: building?.name ?? null,
        sort: building?.sort ?? Number.MAX_SAFE_INTEGER,
        parkings: [],
        sharedWith: [],
        overCapacity: false,
        ...EMPTY_TOTALS,
      }

      const sharedWith = parking.buildings
        .filter(other => String(other.id) !== key)
        .map(other => other.name)

      byBuilding.set(key, {
        ...current,
        parkings: [...current.parkings, parking],
        sharedWith: Array.from(new Set(current.sharedWith.concat(sharedWith))),
        overCapacity: current.overCapacity || parking.overCapacity,
        ...addTotals(current, parking),
      })
    })
  })

  return Array.from(byBuilding.values()).sort(
    (a, b) => a.sort - b.sort || (a.name ?? '').localeCompare(b.name ?? ''),
  )
}

export interface IDateRange {
  from: string
  to: string
}

export const rangeForDays = (days: number): IDateRange => ({
  from: dayjs()
    .subtract(days - 1, 'day')
    .startOf('day')
    .toISOString(),
  to: dayjs().endOf('day').toISOString(),
})

export const previousRangeForDays = (days: number): IDateRange => ({
  from: dayjs()
    .subtract(days * 2 - 1, 'day')
    .startOf('day')
    .toISOString(),
  to: dayjs()
    .subtract(days, 'day')
    .endOf('day')
    .toISOString(),
})

export const todayRange = (): IDateRange => ({
  from: dayjs().startOf('day').toISOString(),
  to: dayjs().endOf('day').toISOString(),
})

export const rangeForDate = (date: string): IDateRange => ({
  from: dayjs(date).startOf('day').toISOString(),
  to: dayjs(date).endOf('day').toISOString(),
})

export const previousRangeForDate = (date: string): IDateRange =>
  rangeForDate(dayjs(date).subtract(1, 'day').format('YYYY-MM-DD'))
